import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui-kit/form';

import { MarkdownEditor } from '@/modules/gpt-chats/components/markdown-editor';
import { useAddText } from '@/modules/gpt-chats/hooks/use-kb';

import { useToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import {
  textKbFormSchema,
  TextKbFormValues,
  textKbFormDefaultValues,
} from '@/modules/gpt-chats/components/agent-knowledge-base/text/utils';

type AddKbWriteupModalProps = {
  folderId: string;
};

export const AddKbWriteupModal = ({ folderId }: AddKbWriteupModalProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const form = useForm<TextKbFormValues>({
    defaultValues: textKbFormDefaultValues,
    resolver: zodResolver(textKbFormSchema),
    mode: 'onChange',
  });

  const { isValid } = form.formState;
  const textValue = form.watch('text') || '';
  const isUnderMinLength = textValue.replace(/\s+/g, ' ').trim().length < 100;

  const { mutateAsync, isPending } = useAddText();

  const validateContent = (value: string): string | undefined => {
    const result = textKbFormSchema.safeParse({ text: value });
    if (!result.success) {
      return result.error.errors[0].message;
    }
    return undefined;
  };

  const submitHandler = async (values: TextKbFormValues) => {
    try {
      if (!folderId) {
        return toast.error({ errors: t('SOMETHING_WENT_WRONG') });
      }

      const res = await mutateAsync({
        ...values,
        folder_id: folderId,
      });

      if (!res.is_success) {
        return toast.error({ errors: res.error });
      }

      toast.success(t('NEW_KNOWLEDGE_ADDED'));
      form.reset();
      setOpen(false);
    } catch (error) {
      toast.error({ errors: ErrorTransformer(error) });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          {t('ADD_WRITEUP')}
        </Button>
      </DialogTrigger>

      <DialogContent className="md:min-w-[720px]">
        <DialogHeader>
          <DialogTitle>{t('ADD_KNOWLEDGE')}</DialogTitle>
          <DialogDescription>{t('ENTER_ACCURATE_INFO_DESCRIPTION')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(submitHandler)}>
          <Form {...form}>
            <FormField
              control={form.control}
              name="text"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <MarkdownEditor
                      value={field.value}
                      onChange={field.onChange}
                      minLength={100}
                      maxLength={10000}
                      showCharacterCount
                      className="h-64"
                      validate={validateContent}
                      placeholder={t('ENTER_KB_CONTENT_PLACEHOLDER')}
                      disabled={isPending}
                      showToolbar
                      showModeToggle
                      defaultMode="edit"
                      isInvalid={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Form>

          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button variant="outline" size="sm">
                {t('CANCEL')}
              </Button>
            </DialogClose>

            <Button
              type="submit"
              size="sm"
              className="min-w-20"
              disabled={isPending || !isValid || isUnderMinLength}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('ADDING')}
                </>
              ) : (
                t('ADD')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
